import { defineStore } from "pinia";
import { v4 as uuidv4 } from "uuid";
import { parsePhoneNumber } from "awesome-phonenumber";
import * as Sentry from "@sentry/browser";
import { useGlobalStore } from "./global";
import analytics from "../services/analytics";

export const WORKFLOW_STEPS = [
  "start",
  "initial-personal-info-gate",
  "questionnaire",
  "audio-test-microphone-and-speaker-test-instructions",
  "audio-test-speaker-test-instructions",
  "audio-test-speaker-test-prompt",
  "audio-test-microphone-test",
  "audio-test-instructions",
  "audio-test",
  "personal-info",
  "result",
];

const NUMBER_OF_STEPS = 5;
const WORDS = [
  "Mountain",
  "Basket",
  "Child",
  "Lobster",
  "Feather",
  "Shoe",
  "Window",
  "Coffee",
  "Ocean",
  "Cat",
  "Flower",
  "Needle",
  "Wagon",
  "Chimney",
  "Star",
];

const QUESTION_GROUPS = {
  memory: {
    "memory-1": {
      code: 0,
      type: "YES_NO",
      text: "Have you or someone else noticed any changes in your memory?",
      imagePath: "noticed-changes-1.svg",
    },
  },
  "recent-memory": {
    "recent-memory-1": {
      code: 1,
      type: "YES_NO",
      text: "Do you misplace items around the house, such as glasses, phone or keys?",
      imagePath: "glasses-3.svg",
    },
    "recent-memory-2": {
      code: 2,
      type: "YES_NO",
      text: "Do you ever walk into a room and forget what you came in there for, such as kitchen or garage?",
      imagePath: "walk-into-room-5.svg",
    },
  },
  "remote-memory": {
    "remote-memory-1": {
      code: 3,
      type: "YES_NO",
      text: "Do you have any difficulty remembering your anniversary date if married, the names of the schools you attended and/or children's date of births, without having to look them up or ask others?",
      imagePath: "repeat-yourself-10.svg",
    },
  },
  aphasia: {
    "aphasia-1": {
      code: 4,
      type: "YES_NO",
      text: "Do you have difficulty finding words in conversation or difficulty naming common items?",
      imagePath: "finding-words-4.svg",
    },
    "aphasia-2": {
      code: 5,
      type: "YES_NO",
      text: "Do you repeat yourself or things you say or have you been told that you are being repetitious?",
      imagePath: "repeat-yourself-10.svg",
    },
  },
  "apraxia-adls": {
    "apraxia-adls-1": {
      code: 6,
      type: "YES_NO",
      text: 'While driving do you ever get mixed up or turned around with directions in well-known places, places you have been several times, such as "Do I turn right or left at the stop sign?" or "Which way do I go?"',
      imagePath: "directions-7.svg",
    },
    "apraxia-adls-2": {
      code: 7,
      type: "YES_NO",
      text: "Have you noticed any deterioration, loss of interest or inability to perform hobbies, crafts, cooking, cleaning or chores?",
      imagePath: "noticed-changes-1.svg",
    },
  },
  "executive-functioning": {
    "executive-functioning-1": {
      code: 8,
      type: "YES_NO",
      text: "Do you find yourself making lists or notes more often and, despite that, forgetting sometimes or still needing reminders?",
      imagePath: "making-notes-2.svg",
    },
    "executive-functioning-2": {
      code: 9,
      type: "YES_NO",
      text: "Have you noticed any changes with your ability to handle or involvement with finances? Such as difficulty paying bills, balancing the checkbook, much slower and more cautious, or notice it is taking much more time than usual?",
      imagePath: "handle-changes-8.svg",
    },
  },
  judgment: {
    "judgment-1": {
      code: 10,
      type: "YES_NO",
      text: "Do you ask others to assist you in solving problems or 'bouncing ideas' off of them more than you used to do?",
      imagePath: "assist-you-9.svg",
    },
  },
};

export const useAssessmentStore = defineStore("assessment", {
  state: () => {
    const globalStore = useGlobalStore();
    const words = globalStore.testingMode ? WORDS.slice(0, 2) : WORDS;

    return {
      id: uuidv4(),
      clinicId: null,
      personalInfo: {
        firstName: globalStore.prefillMode ? "Tester" : null,
        lastName: globalStore.prefillMode ? "Quick" : null,
        emailAddress: globalStore.prefillMode ? "tester@quick.com" : null,
        phoneNumber: globalStore.prefillMode ? "+1 407-999-9999" : null,
        age: globalStore.prefillMode ? 90 : null,
        wasSubmitted: false,
      },
      words,
      questionGroups: Object.entries(QUESTION_GROUPS).map(
        ([groupId, groupQuestions]) => {
          let questions = Object.entries(groupQuestions);

          return {
            id: groupId,
            questions: questions.map(([questionId, question]) => {
              return {
                answer: globalStore.prefillMode
                  ? Math.random() < 0.5
                    ? "YES"
                    : "NO"
                  : null,
                id: questionId,
                ...question,
              };
            }),
          };
        },
      ),
      steps: Array.from(
        {
          length: NUMBER_OF_STEPS,
        },
        (v, k) => k + 1,
      ).map((number) => {
        const assessmentWords = words.map((text) => {
          return {
            id: uuidv4(),
            text,
            wasRecalled: globalStore.testingMode ? Math.random() < 0.5 : false,
          };
        });

        return {
          id: uuidv4(),
          number,
          isCompleted: false,
          assessmentWords,
          recognizedWords: assessmentWords
            .filter((word) => word.wasRecalled)
            .map((word) => {
              return {
                text: word.text,
                isFinal: true,
              };
            }),
        };
      }),
    };
  },
  getters: {
    shouldSkipWorkflowStep: (state) => (workflowStepIdentifier) => {
      const globalStore = useGlobalStore();

      if (!globalStore.skipToAssessmentWorkflowStep) return false;

      const targetWorkflowStepIndex = WORKFLOW_STEPS.indexOf(
        globalStore.skipToAssessmentWorkflowStep,
      );
      const currentWorkflowStepIndex = WORKFLOW_STEPS.indexOf(
        workflowStepIdentifier,
      );

      // Skip if current workflow step comes before the target workflow step
      return (
        targetWorkflowStepIndex >= 0 &&
        currentWorkflowStepIndex >= 0 &&
        currentWorkflowStepIndex < targetWorkflowStepIndex
      );
    },
    recalledAssessmentWords(state) {
      return state.steps.map((step) => step.recalledAssessmentWords).flat();
    },
    questions(state) {
      return state.questionGroups
        .map((questionGroup) => questionGroup.questions)
        .flat();
    },
    totalNumberOfQuestions(state) {
      return this.questions.length;
    },
    answeredQuestions(state) {
      return state.questions.filter((question) => question.answer);
    },
    answeredQuestionsCount(state) {
      return this.answeredQuestions.length;
    },
    currentQuestion(state) {
      return this.questions.find((question) => !question.answer);
    },
    currentQuestionIndex(state) {
      const currentQuestion = this.currentQuestion;

      if (!currentQuestion) {
        return null;
      }

      let index = 1;

      for (const question of this.questions) {
        if (question.id === currentQuestion.id) {
          return index;
        }

        index += 1;
      }

      return null;
    },
    currentStep(state) {
      return state.steps.find((step) => !step.isCompleted);
    },
    completedSteps(state) {
      return state.steps.filter((step) => step.isCompleted);
    },
    completedStepsCount(state) {
      return this.completedSteps.length;
    },
    recalledAssessmentWordsForStep(state) {
      return (stepNumber) => {
        const step = this.findStepByNumber(stepNumber);

        return step.assessmentWords.filter((word) => word.wasRecalled);
      };
    },
    findQuestionById(state) {
      return (questionId) => {
        for (const questionGroup of state.questionGroups) {
          for (const question of questionGroup.questions) {
            if (question.id === questionId) {
              return question;
            }
          }
        }
        return null;
      };
    },
    findStepByNumber(state) {
      return (stepNumber) => {
        return state.steps.find((step) => step.number === stepNumber);
      };
    },
    findAssessmentWordByIdAndStepNumber(state) {
      return (assessmentWordId, stepNumber) => {
        const step = this.findStepByNumber(stepNumber);

        return step.assessmentWords.find(
          (assessmentWord) => assessmentWord.id === assessmentWordId,
        );
      };
    },
    findAssessmentWordByTextAndStepNumber(state) {
      return (wordText, stepNumber) => {
        const step = this.findStepByNumber(stepNumber);

        return step.assessmentWords.find(
          (assessmentWord) => assessmentWord.text === wordText,
        );
      };
    },
    score(state) {
      return state.steps
        .map((step) => step.assessmentWords.filter((word) => word.wasRecalled))
        .flat().length;
    },
    maxPossibleScore(state) {
      return state.steps.map((step) => step.assessmentWords).flat().length;
    },
    nextRouteName(state) {
      if (!state.personalInfo.age) {
        return "assessment-initial-personal-info-gate";
      }
      if (this.currentQuestion) {
        return "assessment-questionnaire";
      }
      if (this.currentStep) {
        return "assessment-audio-test";
      }
      if (!state.personalInfo.wasSubmitted) {
        return "assessment-personal-info";
      }

      return "assessment-result";
    },
  },
  actions: {
    updatePersonalInfo(personalInfo) {
      for (const p in this.personalInfo) {
        if (personalInfo.hasOwnProperty(p)) {
          let value = personalInfo[p];

          if (p === "phoneNumber") {
            const phoneNumber = parsePhoneNumber(value, { regionCode: "US" });
            if (phoneNumber.valid) {
              value = phoneNumber.number.international;
            }
          }

          this.personalInfo[p] = value;
        }
      }
    },
    submitPersonalInfo() {
      this.personalInfo.wasSubmitted = true;
    },
    saveQuestionnaireAnswerForCurrentQuestion(answer) {
      const currentQuestion = this.currentQuestion;

      if (currentQuestion) {
        currentQuestion.answer = answer;

        const globalStore = useGlobalStore();

        // In testing mode, auto-complete remaining questions with test data after 2 questions are answered
        if (globalStore.testingMode) {
          if (this.answeredQuestionsCount >= 2) {
            // Auto-complete all remaining questions with test data
            this.questions.forEach((question, index) => {
              if (question.answer === null) {
                // Alternate between "YES" and "NO" answers for variety in test data
                question.answer = index % 2 === 0 ? "YES" : "NO";
              }
            });
          }
        }
      }
    },
    saveRecognizedWordForCurrentStep(word, isFinal) {
      const lowerCasedWord = word.toLowerCase();
      const matchingAssessmentWord = this.currentStep.assessmentWords.find(
        (assessmentWord) =>
          assessmentWord.text.toLowerCase() === lowerCasedWord,
      );

      if (matchingAssessmentWord || isFinal) {
        this.currentStep.recognizedWords.push(word);
      }
      if (matchingAssessmentWord) {
        matchingAssessmentWord.wasRecalled = true;
      }
    },
    markCurrentStepAsCompleted() {
      const globalStore = useGlobalStore();

      this.currentStep.isCompleted = true;

      // In testing mode, auto-complete remaining steps with test data after 2 steps are completed
      if (globalStore.testingMode) {
        if (this.completedStepsCount >= 2) {
          // Auto-complete all remaining steps with test data
          this.steps.forEach((step, index) => {
            if (!step.isCompleted) {
              // Mark step as completed
              step.isCompleted = true;

              // Add test data: mark some words as recalled (alternating pattern for variety)
              step.assessmentWords.forEach((word, wordIndex) => {
                // Mark every other word as recalled, starting with the first word
                if (wordIndex % 2 === 0) {
                  word.wasRecalled = true;
                  step.recognizedWords.push(word.text);
                }
              });
            }
          });
        }
      }
    },
    async submitAssessment() {
      const data = {
        assessment: this.$state,
      };

      try {
        analytics.track("Submit Assessment", {
          score: this.score,
          maxPossibleScore: this.maxPossibleScore,
        });

        const response = await fetch(`/api/${this.clinicId}/assessments`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
        const responseData = await response.json();
      } catch (error) {
        Sentry.captureException(error);
        console.error(error);
      }
    },
  },
});
